import { DollarSign, TrendingUp, ShoppingCart, Receipt, Tag, Gift, RotateCcw, TrendingDown } from 'lucide-react';

export function KPICards({ data }) {
  // Create a default summary object with all values set to 0
  const defaultSummary = {
    Gross: 0,
    NetSale: 0,
    TotalOrders_Landed: 0,
    Tax: 0,
    Discount: 0,
    FocAmount: 0,
    SaleReturnAmount: 0,
    DeliveryCharges: 0
  };

  // Merge provided data with default values
  const summary = {
    ...defaultSummary,
    ...(data?.summary || {})
  };

  // Also check if we have data from Table (the raw response might have different field names)
  // Some APIs use camelCase vs PascalCase
  const rawSummary = data?.summary || {};
  
  // Map the field names correctly (handle different naming conventions)
  const gross = rawSummary.Gross || rawSummary.gross || rawSummary.GROSS || 0;
  const netSale = rawSummary.NetSale || rawSummary.netSale || rawSummary.NETSALE || rawSummary.NetSales || 0;
  const totalOrders = rawSummary.TotalOrders_Landed || rawSummary.totalOrders || rawSummary.TOTALORDERS || rawSummary.TotalOrders || 0;
  const tax = rawSummary.Tax || rawSummary.tax || rawSummary.TAX || 0;
  const discount = rawSummary.Discount || rawSummary.discount || rawSummary.DISCOUNT || 0;
  const focAmount = rawSummary.FocAmount || rawSummary.focAmount || rawSummary.FOCAMOUNT || rawSummary.Foc || 0;
  const saleReturnAmount = rawSummary.SaleReturnAmount || rawSummary.saleReturnAmount || rawSummary.SALERETURNAMOUNT || rawSummary.Returns || 0;
  const deliveryCharges = rawSummary.DeliveryCharges || rawSummary.deliveryCharges || rawSummary.DELIVERYCHARGES || 0;

  const kpis = [
    {
      title: 'Gross Sales',
      value: gross,
      icon: DollarSign,
      color: 'from-blue-500 to-blue-600',
      trend: '+12.5%',
      trendUp: true
    },
    {
      title: 'Net Sales',
      value: netSale,
      icon: TrendingUp,
      color: 'from-[#01796d] to-teal-600',
      trend: '+8.3%',
      trendUp: true
    },
    {
      title: 'Total Orders',
      value: totalOrders,
      icon: ShoppingCart,
      color: 'from-purple-500 to-purple-600',
      trend: '+15.2%',
      trendUp: true,
      isCount: true
    },
    {
      title: 'Total Tax',
      value: tax,
      icon: Receipt,
      color: 'from-orange-500 to-orange-600',
      trend: '+10.1%',
      trendUp: true
    },
    {
      title: 'Discounts',
      value: discount,
      icon: Tag,
      color: 'from-pink-500 to-pink-600',
      trend: '-3.2%',
      trendUp: false
    },
    {
      title: 'FOC Amount',
      value: focAmount,
      icon: Gift,
      color: 'from-indigo-500 to-indigo-600',
      trend: '+5.7%',
      trendUp: true
    },
    {
      title: 'Returns',
      value: saleReturnAmount,
      icon: RotateCcw,
      color: 'from-red-500 to-red-600',
      trend: '-2.1%',
      trendUp: true
    },
    {
      title: 'Delivery Charges',
      value: deliveryCharges,
      icon: TrendingDown,
      color: 'from-cyan-500 to-cyan-600',
      trend: '+6.8%',
      trendUp: true
    }
  ];

  const formatValue = (value, isCount) => {
    // Ensure value is a number
    const numValue = Number(value) || 0;
    
    if (isCount) {
      return numValue.toLocaleString();
    }
    return `Rs ${numValue.toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map((kpi, index) => {
        const Icon = kpi.icon;
        return (
          <div
            key={index}
            className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
          >
            <div 
              className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-300"
              style={{ 
                backgroundImage: `linear-gradient(to bottom right, ${
                  kpi.color.includes('from-') ? 
                  kpi.color.replace('from-', '') : 
                  '#01796d'
                })` 
              }}
            ></div>
            
            <div className="flex items-start justify-between mb-4">
              <div className={`bg-gradient-to-br ${kpi.color} p-3 rounded-xl shadow-lg`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                kpi.trendUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {kpi.trend}
                {kpi.trendUp ? '↑' : '↓'}
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-slate-500 font-medium">{kpi.title}</p>
              <p className="text-2xl font-bold text-slate-900">
                {formatValue(kpi.value, kpi.isCount)}
              </p>
            </div>

            {/* Show zero indicator if value is zero */}
            {kpi.value === 0 && (
              <div className="absolute bottom-2 right-2">
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
                  No data
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}